// frontend/packages/business-console/src/pages/Visitors.jsx
//
// Analytics: "traffic you can trace to revenue" (design Page 11). A single
// command dashboard, not a tabbed report. Recreated faithfully from
// `Analytics Redesign.dc.html` using the design tokens + primitive kit; no
// chart library (hand-rolled SVG).
//
// Layout (top -> bottom): header (title + subtitle + period toggle + Export)
// -> realtime strip -> 5 KPI cards -> hero revenue funnel -> two columns
// (traffic chart + top-pages-by-revenue | channels + searches) -> bottom row
// (service area | devices | when-leads-arrive).
//
// Data: every panel with a backend reads it (summary, timeseries, funnel,
// top-pages, sources, attribution + the pipeline pulse for quotes). Panels
// whose first-party events are not captured yet (live-on-site, search terms,
// service area, devices, lead-time heatmap) render faithfully but are tagged
// "Sample" until that instrumentation lands. Demo mode (`?demo=1`) fills
// everything from the shared fixtures.
//
// See docs/design/site-analytics.md + the Analytics handoff spec.

import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import {
  getVisitorsTimeseries,
  getVisitorsFunnel,
  getVisitorsTopPages,
  getVisitorsSummary,
  getVisitorsAttribution,
  getVisitorsDevices,
  getVisitorsLeadTimes,
  getVisitorsLive,
  getVisitorsAi,
  getMoneyBrief,
  getRoas,
  getBenchmarks,
  updateAdSpend,
} from '../services/visitorsService';
import { fetchPipelinePulse } from '../services/pulseService';
import { BusinessContext } from '../contexts/BusinessContext';
import { demoVisitorsPage } from './Pulse/demoData';
import { useTokens, Surface, StatusPill } from '../components/primitives';

// ── demo / range helpers ──────────────────────────────────────────────────

function readDemoFlag(searchString) {
  const params = new URLSearchParams(searchString || '');
  if (params.get('demo') === '1') return true;
  try {
    return localStorage.getItem('pulse.demo') === '1';
  } catch {
    return false;
  }
}

const PERIODS = [
  { days: 7, label: '7d' },
  { days: 30, label: '30d' },
  { days: 90, label: '90d' },
];

const num = (n) => (typeof n === 'number' && Number.isFinite(n) ? n : 0);
const fmtInt = (n) => num(n).toLocaleString();

// "$41.9k" / "$3,810" style from integer cents.
function fmtUsdShort(cents) {
  const d = num(cents) / 100;
  if (d >= 10000) return `$${(d / 1000).toFixed(1)}k`;
  return `$${Math.round(d).toLocaleString()}`;
}
function fmtUsd(cents) {
  return `$${Math.round(num(cents) / 100).toLocaleString()}`;
}

// Short relative-time label for the live strip ("40s ago", "5m ago").
function agoLabel(iso) {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return null;
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

// Demo fixture for attribution (real data from /v2/visitors/attribution).
// Demo per-engine split of the AI assistants channel (W5).
const DEMO_AI_ENGINES = [
  { engine: 'chatgpt', label: 'ChatGPT', customers: 5, revenueCents: 640000 },
  {
    engine: 'perplexity',
    label: 'Perplexity',
    customers: 3,
    revenueCents: 360000,
  },
  { engine: 'gemini', label: 'Gemini', customers: 1, revenueCents: 120000 },
];

const DEMO_ATTRIBUTION = {
  windowDays: 30,
  totals: {
    revenueCents: 4190000,
    customers: 11,
    attributedRevenueCents: 4190000,
    unattributedCustomers: 2,
  },
  topPages: [
    { path: '/storm', customers: 11, revenueCents: 1840000 },
    { path: '/blog/5-signs-your-tree', customers: 7, revenueCents: 920000 },
    { path: '/services', customers: 6, revenueCents: 610000 },
    { path: '/', customers: 8, revenueCents: 480000 },
    {
      path: '/blog/how-we-price-tree-removal',
      customers: 3,
      revenueCents: 340000,
    },
  ],
  channels: [
    { channel: 'Google search', customers: 21, revenueCents: 1940000 },
    {
      channel: 'AI assistants',
      customers: 9,
      revenueCents: 1120000,
      engines: DEMO_AI_ENGINES,
    },
    { channel: 'Direct', customers: 4, revenueCents: 510000 },
    { channel: 'GBP/Maps', customers: 3, revenueCents: 400000 },
    { channel: 'Referral', customers: 2, revenueCents: 220000 },
  ],
  fromAI: { customers: 9, revenueCents: 1120000, engines: DEMO_AI_ENGINES },
};

// Demo fixture for the money brief (real data from /v2/visitors/money-brief).
const DEMO_BRIEF = {
  windowDays: 30,
  revenueCents: 4190000,
  customers: 11,
  headline: '$41,900 collected from 11 customers in the last 30 days.',
  topPage: { path: '/storm', revenueCents: 1840000, customers: 11 },
  topChannel: {
    channel: 'Google search',
    revenueCents: 1940000,
    customers: 21,
  },
  fromAI: { customers: 9, revenueCents: 1120000 },
  lines: [
    {
      text: 'Your top earning page: /storm brought $18,400 from 11 customers.',
      tone: 'positive',
    },
    {
      text: 'Most revenue came through Google search: $19,400.',
      tone: 'neutral',
    },
    {
      text: 'AI assistants drove 9 customers worth $11,200.',
      tone: 'positive',
    },
    {
      text: '2 paying customers could not be traced back to a page yet.',
      tone: 'opportunity',
    },
  ],
};

// "What made you money" lead card: the plain-English revenue synthesis that
// only UpRyzze can produce (it owns both the site and the payments).
function MoneyBriefCard({ brief }) {
  const t = useTokens();
  if (!brief) return null;
  const toneColor = {
    positive: t.green,
    opportunity: t.amber,
    neutral: t.muted,
  };
  return (
    <Surface radius="14px" sx={{ mt: 1.75, p: { xs: 2, sm: 2.5 } }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: '.6px',
            textTransform: 'uppercase',
            color: t.faint,
          }}
        >
          What made you money
        </Typography>
        <Typography sx={{ fontSize: 10.5, color: t.faint }}>
          &middot; last {brief.windowDays} days
        </Typography>
      </Box>
      <Typography
        sx={{
          fontSize: 17,
          fontWeight: 900,
          letterSpacing: '-0.4px',
          color: t.text,
          mb: brief.lines && brief.lines.length ? 1.25 : 0,
        }}
      >
        {brief.headline}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.625 }}>
        {(brief.lines || []).map((l) => (
          <Box
            key={l.text}
            sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                mt: '6px',
                flexShrink: 0,
                bgcolor: toneColor[l.tone] || t.muted,
              }}
            />
            <Typography
              sx={{ fontSize: 13, color: t.textSecondary, fontWeight: 500 }}
            >
              {l.text}
            </Typography>
          </Box>
        ))}
      </Box>
    </Surface>
  );
}

// Sample data for panels whose first-party events are not captured yet. Shown
// with a "Sample" chip in real mode so it is never mistaken for live data.
const SAMPLE_SEARCHES = [
  { term: 'emergency tree removal near me', visits: 210, leads: 6 },
  { term: 'tree leaning after storm', visits: 142, leads: 4 },
  { term: 'how much to remove a tree', visits: 138, leads: 3 },
  { term: 'stump grinding maplewood', visits: 96, leads: 2 },
  { term: 'is my tree dead or dormant', visits: 64, leads: 1 },
];
const SAMPLE_AREA = [
  { town: 'Maplewood', leads: 12, pct: 100, out: false },
  { town: 'Riverton', leads: 8, pct: 70, out: false },
  { town: 'Oak Hill', leads: 6, pct: 55, out: false },
  { town: 'Westbrook', leads: 5, pct: 47, out: false },
  { town: 'Cedar Falls', leads: 3, pct: 32, out: false },
  { town: 'Outside 25mi', leads: 5, pct: 49, out: true },
];
const SAMPLE_DEVICES = [
  { label: 'Mobile', pct: 64 },
  { label: 'Desktop', pct: 31 },
  { label: 'Tablet', pct: 5 },
];
// Mon-Fri x AM/Noon/Eve/Late, 0..3 intensity.
const SAMPLE_HEAT = [
  { day: 'Mon', cells: [1, 1, 2, 1] },
  { day: 'Tue', cells: [1, 2, 3, 2] },
  { day: 'Wed', cells: [1, 2, 3, 2] },
  { day: 'Thu', cells: [1, 2, 3, 2] },
  { day: 'Fri', cells: [0, 1, 1, 0] },
];

// Demo fixture for AI crawler activity (real data from /v2/visitors/ai).
const DEMO_AI = {
  days: 30,
  totalHits: 312,
  totalTraining: 218,
  totalRuntime: 94,
  byBot: [
    { name: 'GPTBot', hits: 124 },
    { name: 'ClaudeBot', hits: 58 },
    { name: 'Google-Extended', hits: 41 },
    { name: 'PerplexityBot', hits: 34 },
    { name: 'ChatGPT-User', hits: 28 },
    { name: 'Perplexity-User', hits: 17 },
    { name: 'CCBot', hits: 10 },
  ],
  sparkline: [
    { date: '1', hits: 8 },
    { date: '2', hits: 11 },
    { date: '3', hits: 9 },
    { date: '4', hits: 14 },
    { date: '5', hits: 12 },
    { date: '6', hits: 18 },
    { date: '7', hits: 22 },
  ],
};

// Demo fixture for ROAS (real data from /v2/visitors/roas).
const DEMO_ROAS = {
  windowDays: 30,
  rows: [
    {
      channel: 'Social',
      spendCents: 300000,
      revenueCents: 1120000,
      customers: 9,
      roas: 3.73,
    },
    {
      channel: 'Google search',
      spendCents: 600000,
      revenueCents: 1940000,
      customers: 21,
      roas: 3.23,
    },
    {
      channel: 'Direct',
      spendCents: 0,
      revenueCents: 510000,
      customers: 4,
      roas: null,
    },
  ],
  totals: { spendCents: 900000, revenueCents: 3570000, roas: 3.97 },
};

function roasColor(roas, t) {
  if (roas == null) return t.faint;
  if (roas >= 2) return t.green;
  if (roas >= 1) return t.amber;
  return t.red;
}

// How you compare: this site's visit-to-lead conversion rate vs the published
// median for its industry vertical (ask #2). The comparison baseline is public
// industry data, so it is honest from tenant #1. Renders nothing unless the
// platform switch is on (data.enabled) and the tenant has enough of their own
// traffic for a stable rate (data.hasEnoughData).
function BenchmarkCard({ data }) {
  const t = useTokens();
  if (!data || !data.enabled || !data.hasEnoughData) return null;
  const c = data.conversion || {};
  const verdictMeta = {
    top: { color: t.green, word: 'well above average' },
    above: { color: t.green, word: 'above average' },
    about: { color: t.amber, word: 'about average' },
    below: { color: t.red, word: 'below average' },
  };
  const meta = verdictMeta[c.verdict] || verdictMeta.about;
  const ratioText =
    typeof c.ratio === 'number' && c.ratio > 0
      ? `${c.ratio}x the typical ${data.verticalLabel.toLowerCase()} rate`
      : null;

  return (
    <Surface radius="14px" sx={{ mt: 1.75, p: { xs: 2, sm: 2.5 } }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: '.6px',
            textTransform: 'uppercase',
            color: t.faint,
          }}
        >
          How you compare
        </Typography>
        <Typography sx={{ fontSize: 10.5, color: t.faint }}>
          &middot; {data.verticalLabel}
          {data.window?.days ? ` · last ${data.window.days} days` : ''}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.25, mb: 0.5 }}>
        <Typography
          sx={{
            fontSize: 30,
            fontWeight: 900,
            letterSpacing: '-0.6px',
            color: meta.color,
            lineHeight: 1,
          }}
        >
          {c.youPct}%
        </Typography>
        <Typography sx={{ fontSize: 13, color: t.textSecondary }}>
          your visit-to-lead rate
        </Typography>
      </Box>

      <Typography
        sx={{ fontSize: 13.5, color: t.text, fontWeight: 600, mb: 0.75 }}
      >
        You convert {meta.word}.{' '}
        {ratioText ? (
          <Box
            component="span"
            sx={{ color: t.textSecondary, fontWeight: 500 }}
          >
            That is {ratioText}.
          </Box>
        ) : null}
      </Typography>

      <Typography sx={{ fontSize: 12.5, color: t.textSecondary }}>
        Typical for {data.verticalLabel.toLowerCase()}: about{' '}
        {c.industryMedianPct}% (a strong site clears {c.industryStrongPct}%).
      </Typography>

      <Typography sx={{ fontSize: 10.5, color: t.faint, mt: 1 }}>
        Source: {data.source}
        {data.asOf ? ` (${data.asOf})` : ''}
        {data.provisional ? '. Figures are provisional.' : ''}
      </Typography>
    </Surface>
  );
}

// Return-on-ad-spend card: closed-dollar return per channel. Spend is
// entered inline (manual v1); revenue + ROAS come from attribution. Only
// businessadmin can save (server-enforced); a 403 surfaces a small note.
function RoasCard({ roas, onSaveSpend }) {
  const t = useTokens();
  const rows = (roas && roas.rows) || [];
  const totals = (roas && roas.totals) || {};
  const [drafts, setDrafts] = useState({});
  const [saveError, setSaveError] = useState('');

  if (!rows.length) return null;

  const dollarsOf = (cents) =>
    typeof cents === 'number' ? String(Math.round(cents / 100)) : '0';

  const commit = async (channel) => {
    const raw = drafts[channel];
    if (raw == null) return;
    const dollars = Math.max(0, Math.round(Number(raw)));
    if (!Number.isFinite(dollars)) return;
    setSaveError('');
    try {
      await onSaveSpend(channel, dollars * 100);
      setDrafts((d) => {
        const next = { ...d };
        delete next[channel];
        return next;
      });
    } catch {
      setSaveError('Only admins can edit ad spend.');
    }
  };

  return (
    <Surface radius="14px" sx={{ mt: 1.75, p: { xs: 2, sm: 2.5 } }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1.25 }}>
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: '.6px',
            textTransform: 'uppercase',
            color: t.faint,
          }}
        >
          Return on ad spend
        </Typography>
        <Typography sx={{ fontSize: 10.5, color: t.faint }}>
          &middot; spend you enter vs revenue it earned
        </Typography>
        {typeof totals.roas === 'number' ? (
          <Typography
            sx={{
              ml: 'auto',
              fontSize: 13,
              fontWeight: 900,
              color: roasColor(totals.roas, t),
            }}
          >
            {totals.roas.toFixed(1)}x overall
          </Typography>
        ) : null}
      </Box>

      {/* header */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 110px 1fr 64px',
          gap: 1,
          px: 0.5,
          pb: 0.5,
          fontSize: 10,
          fontWeight: 800,
          letterSpacing: '.4px',
          textTransform: 'uppercase',
          color: t.faint,
        }}
      >
        <Box>Channel</Box>
        <Box>Monthly spend</Box>
        <Box sx={{ textAlign: 'right' }}>Revenue</Box>
        <Box sx={{ textAlign: 'right' }}>ROAS</Box>
      </Box>

      {rows.map((r) => {
        const draft = drafts[r.channel];
        const value = draft != null ? draft : dollarsOf(r.spendCents);
        return (
          <Box
            key={r.channel}
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 110px 1fr 64px',
              gap: 1,
              alignItems: 'center',
              px: 0.5,
              py: 0.75,
              borderTop: `1px solid ${t.hairline}`,
            }}
          >
            <Typography
              sx={{ fontSize: 13, fontWeight: 700, color: t.text }}
              noWrap
            >
              {r.channel}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                border: `1px solid ${t.cardBorder}`,
                borderRadius: '8px',
                px: 1,
                bgcolor: t.card,
              }}
            >
              <Typography sx={{ fontSize: 12, color: t.faint }}>$</Typography>
              <input
                value={value}
                inputMode="numeric"
                onChange={(e) =>
                  setDrafts((d) => ({
                    ...d,
                    [r.channel]: e.target.value.replace(/[^0-9]/g, ''),
                  }))
                }
                onBlur={() => commit(r.channel)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') e.currentTarget.blur();
                }}
                style={{
                  border: 'none',
                  outline: 'none',
                  width: '100%',
                  background: 'transparent',
                  font: 'inherit',
                  fontSize: 13,
                  color: t.text,
                  padding: '6px 0',
                }}
              />
            </Box>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 700,
                color: t.text,
                textAlign: 'right',
              }}
            >
              ${Math.round((r.revenueCents || 0) / 100).toLocaleString()}
            </Typography>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 900,
                textAlign: 'right',
                color: roasColor(r.roas, t),
              }}
            >
              {r.roas == null ? '-' : `${r.roas.toFixed(1)}x`}
            </Typography>
          </Box>
        );
      })}

      <Typography sx={{ fontSize: 10.5, color: t.faint, mt: 1.25 }}>
        {saveError
          ? saveError
          : 'Enter what you spend per channel each month. Automatic ad-account linking is coming.'}
      </Typography>
    </Surface>
  );
}

export default function Visitors() {
  const t = useTokens();
  const { business } = useContext(BusinessContext);
  const location = useLocation();
  const navigate = useNavigate();
  const basePath = business?.slug ? `/business/${business.slug}` : '';
  const isDemo = useMemo(
    () => readDemoFlag(location.search),
    [location.search]
  );

  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState(null);
  const [timeseries, setTimeseries] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [topPages, setTopPages] = useState(null);
  const [attribution, setAttribution] = useState(null);
  const [pipeline, setPipeline] = useState(null);
  const [devices, setDevices] = useState(null);
  const [leadTimes, setLeadTimes] = useState(null);
  const [live, setLive] = useState(null);
  const [moneyBrief, setMoneyBrief] = useState(null);
  const [roas, setRoas] = useState(null);
  const [aiBots, setAiBots] = useState(null);
  const [benchmark, setBenchmark] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (isDemo) {
      setSummary(demoVisitorsPage.summary);
      setTimeseries(demoVisitorsPage.timeseries);
      setFunnel(demoVisitorsPage.funnel);
      setTopPages(demoVisitorsPage.topPages);
      setAttribution(DEMO_ATTRIBUTION);
      setMoneyBrief(DEMO_BRIEF);
      setRoas(DEMO_ROAS);
      setAiBots(DEMO_AI);
      setPipeline({ sentCount: 24, acceptedCount: 11 });
      setDevices({
        total: 100,
        devices: SAMPLE_DEVICES.map((d) => ({
          label: d.label,
          count: d.pct,
          pct: d.pct,
        })),
      });
      setLeadTimes({ max: 3, rows: SAMPLE_HEAT });
      setLive({
        onSiteNow: 14,
        topPath: '/storm',
        topPathReaders: 3,
        lastFormStartAt: new Date(Date.now() - 40000).toISOString(),
      });
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }
    if (!business?._id) return undefined;
    setLoading(true);
    (async () => {
      const [s, ts, fu, tp, attr, pl, dv, lt, lv, mb, ro, ai, bm] =
        await Promise.all([
          getVisitorsSummary().catch(() => null),
          getVisitorsTimeseries(days).catch(() => null),
          getVisitorsFunnel(days).catch(() => null),
          getVisitorsTopPages(days).catch(() => null),
          getVisitorsAttribution(days).catch(() => null),
          fetchPipelinePulse().catch(() => null),
          getVisitorsDevices(days).catch(() => null),
          getVisitorsLeadTimes(days).catch(() => null),
          getVisitorsLive().catch(() => null),
          getMoneyBrief(days).catch(() => null),
          getRoas(days).catch(() => null),
          getVisitorsAi(days).catch(() => null),
          getBenchmarks(days).catch(() => null),
        ]);
      if (cancelled) return;
      setSummary(s);
      setTimeseries(ts);
      setFunnel(fu);
      setTopPages(tp);
      setAttribution(attr);
      setPipeline(pl);
      setDevices(dv);
      setLeadTimes(lt);
      setLive(lv);
      setMoneyBrief(mb);
      setRoas(ro);
      setAiBots(ai);
      setBenchmark(bm);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [business?._id, days, isDemo]);

  // Save ad spend then refresh ROAS (no full reload; demo is read-only).
  const handleSaveSpend = async (channel, monthlySpendCents) => {
    if (isDemo) return;
    await updateAdSpend(channel, monthlySpendCents);
    const ro = await getRoas(days).catch(() => null);
    if (ro) setRoas(ro);
  };

  // Live strip refreshes on its own short cadence (the rest is range-bound).
  useEffect(() => {
    if (isDemo || !business?._id) return undefined;
    let cancelled = false;
    const tick = () => {
      if (document.visibilityState !== 'visible') return;
      getVisitorsLive()
        .then((d) => {
          if (!cancelled && d) setLive(d);
        })
        .catch(() => {});
    };
    const timer = setInterval(tick, 30000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [business?._id, isDemo]);

  // ── derived metrics (computed from real endpoints; reconcile per §18a) ──
  const tsItems = timeseries?.items || [];
  const visitors = useMemo(
    () => tsItems.reduce((a, r) => a + num(r.uniqueVisitors || r.sessions), 0),
    [tsItems]
  );
  // Authoritative lead count (server-recorded FormSubmission, from the funnel
  // endpoint's `leads`); fall back to the client-event submit count only if an
  // older backend hasn't shipped `leads` yet. See discovery 14 (F-B).
  const leads = num(funnel?.leads ?? funnel?.formSubmits);
  const visitToLead = visitors > 0 ? (leads / visitors) * 100 : 0;
  const wonJobs = num(attribution?.totals?.customers);
  const revenueCents = num(attribution?.totals?.attributedRevenueCents);
  const avgJobCents = wonJobs > 0 ? revenueCents / wonJobs : 0;

  const todayVisits = num(summary?.today?.sessions);
  const todayLeads = num(summary?.today?.formSubmits);

  const maxDevicePct = Math.max(
    1,
    ...(devices?.devices || []).map((d) => num(d.pct))
  );

  const aiMaxBot = Math.max(
    1,
    ...(aiBots?.byBot || []).map((b) => num(b.hits))
  );

  const viewsByPath = useMemo(() => {
    const m = {};
    for (const p of topPages?.items || []) m[p.path] = num(p.views);
    return m;
  }, [topPages]);

  const channelTotalCust = useMemo(
    () =>
      (attribution?.channels || []).reduce((a, c) => a + num(c.customers), 0),
    [attribution]
  );

  // Funnel stages, wired where we have data. Quotes from the pipeline pulse.
  const funnelStages = useMemo(() => {
    const formStarts = num(funnel?.formStarts);
    const dropped = Math.max(0, formStarts - leads);
    return [
      {
        key: 'visitors',
        label: 'Visitors',
        value: visitors,
        sub: 'unique',
        tone: 'blue',
      },
      {
        key: 'engaged',
        label: 'Engaged',
        value: num(funnel?.scrolled75),
        sub: '2+ pages',
        tone: 'blue',
      },
      {
        key: 'starts',
        label: 'Form starts',
        value: formStarts,
        sub: dropped ? `${fmtInt(dropped)} dropped` : 'started',
        tone: 'purple',
        warn: dropped > 0,
      },
      {
        key: 'leads',
        label: 'Leads',
        value: leads,
        sub: 'into Inbox',
        tone: 'green',
      },
      {
        key: 'quotes',
        label: 'Quotes',
        value: num(pipeline?.sentCount),
        sub: 'sent',
        tone: 'green',
      },
      {
        key: 'won',
        label: 'Won',
        value: revenueCents,
        sub: `${fmtInt(wonJobs)} jobs`,
        tone: 'gradient',
        money: true,
      },
    ];
  }, [funnel, leads, visitors, pipeline, revenueCents, wonJobs]);

  const droppedForms = Math.max(0, num(funnel?.formStarts) - leads);

  return (
    <Box sx={{ p: { xs: 2, md: '20px 24px' }, maxWidth: 1180, mx: 'auto' }}>
      {/* ── header ── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: 24,
              fontWeight: 900,
              letterSpacing: '-0.6px',
              color: t.text,
            }}
          >
            Analytics
          </Typography>
          <Typography
            sx={{ fontSize: 12.5, color: t.muted, fontWeight: 600, mt: '2px' }}
          >
            First-party &middot;{' '}
            <Box component="span" sx={{ color: t.green, fontWeight: 700 }}>
              site to lead to invoice, one visitor
            </Box>
          </Typography>
        </Box>
        <Box
          sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1.125 }}
        >
          {isDemo ? <StatusPill kind="info" label="Demo data" /> : null}
          <Box
            sx={{
              display: 'inline-flex',
              bgcolor: t.subtle,
              borderRadius: '9px',
              p: '3px',
            }}
          >
            {PERIODS.map((p) => {
              const active = p.days === days;
              return (
                <Box
                  key={p.days}
                  onClick={() => setDays(p.days)}
                  sx={{
                    cursor: 'pointer',
                    fontSize: 12,
                    fontWeight: active ? 700 : 600,
                    color: active ? t.text : t.muted,
                    bgcolor: active ? t.card : 'transparent',
                    borderRadius: '7px',
                    px: 1.5,
                    py: '5px',
                    boxShadow: active ? t.smallShadow : 'none',
                  }}
                >
                  {p.label}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {loading ? (
        <Typography sx={{ color: t.muted, fontSize: 13, mt: 3 }}>
          Loading...
        </Typography>
      ) : (
        <>
          {/* ── what made you money (lead synthesis) ── */}
          <MoneyBriefCard brief={moneyBrief} />

          {/* ── how you compare (public industry benchmark) ── */}
          <BenchmarkCard data={benchmark} />

          {/* ── return on ad spend (manual spend entry) ── */}
          <RoasCard roas={roas} onSaveSpend={handleSaveSpend} />

          {/* ── realtime strip ── */}
          <Surface
            radius="12px"
            sx={{
              mt: 1.75,
              p: '11px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flexWrap: 'wrap',
            }}
          >
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.875,
                fontSize: 11.5,
                fontWeight: 800,
                color: t.green,
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: t.green,
                  boxShadow: `0 0 0 4px ${t.greenBg}`,
                }}
              />
              LIVE
            </Box>
            <Typography sx={{ fontSize: 13, color: t.text }}>
              <b style={{ fontSize: 14 }}>{fmtInt(live?.onSiteNow)}</b> on the
              site right now
            </Typography>
            {live?.topPath ? (
              <>
                <Box sx={{ width: '1px', height: 16, bgcolor: t.hairline }} />
                <Typography sx={{ fontSize: 12.5, color: t.muted }}>
                  <Box component="span" sx={{ color: t.text, fontWeight: 800 }}>
                    {fmtInt(live.topPathReaders)}
                  </Box>{' '}
                  reading{' '}
                  <Box component="span" sx={{ color: t.blue, fontWeight: 700 }}>
                    {live.topPath}
                  </Box>
                </Typography>
              </>
            ) : null}
            {agoLabel(live?.lastFormStartAt) ? (
              <>
                <Box sx={{ width: '1px', height: 16, bgcolor: t.hairline }} />
                <Typography sx={{ fontSize: 12.5, color: t.muted }}>
                  last estimate started {agoLabel(live.lastFormStartAt)}
                </Typography>
              </>
            ) : null}
            <Typography
              sx={{
                ml: 'auto',
                fontSize: 11,
                color: t.faint,
                fontWeight: 600,
              }}
            >
              Today: {fmtInt(todayVisits)} visits &middot; {fmtInt(todayLeads)}{' '}
              leads
            </Typography>
          </Surface>

          {/* ── KPI strip ── */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' },
              gap: 1.25,
              mt: 1.5,
            }}
          >
            <KpiCard
              t={t}
              label="Visitors"
              value={fmtInt(visitors)}
              spark={tsItems}
              acc="sessions"
              color={t.blue}
            />
            <KpiCard
              t={t}
              label="Leads"
              value={fmtInt(leads)}
              spark={tsItems}
              acc="formSubmits"
              color={t.green}
            />
            <KpiCard
              t={t}
              label="Visit to lead"
              value={`${visitToLead.toFixed(1)}%`}
              foot="of visitors became leads"
            />
            <KpiCard
              t={t}
              label="Avg job value"
              value={fmtUsd(avgJobCents)}
              foot="from won jobs"
            />
            <KpiCard
              t={t}
              label="Revenue attributed"
              value={fmtUsdShort(revenueCents)}
              foot={`${fmtInt(wonJobs)} jobs won, traced to the site`}
              gradient
            />
          </Box>

          {/* ── hero funnel ── */}
          <Surface radius="14px" sx={{ mt: 1.5, p: '16px 18px' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                flexWrap: 'wrap',
              }}
            >
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 800,
                  letterSpacing: '-0.2px',
                  color: t.text,
                }}
              >
                From visit to paid job
              </Typography>
              <Box
                component="span"
                sx={{
                  fontSize: 10,
                  fontWeight: 800,
                  color: t.purple,
                  bgcolor: t.purpleBg,
                  border: `1px solid ${t.purpleBorder}`,
                  px: 1,
                  py: '2px',
                  borderRadius: '5px',
                  letterSpacing: '.3px',
                }}
              >
                NATIVE ATTRIBUTION
              </Box>
              <Typography
                sx={{
                  ml: 'auto',
                  fontSize: 11,
                  color: t.faint,
                  fontWeight: 600,
                }}
              >
                last {days} days &middot; one connected pipeline
              </Typography>
            </Box>
            <FunnelRow t={t} stages={funnelStages} />
            {droppedForms > 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.875,
                  mt: 1.5,
                  fontSize: 11.5,
                  color: t.textSecondary,
                  bgcolor: t.amberBg,
                  border: `1px solid ${t.amberBorder}`,
                  borderRadius: '9px',
                  p: '8px 12px',
                }}
              >
                <Box component="span" sx={{ color: t.amber, fontWeight: 900 }}>
                  &#9888;
                </Box>
                <span>
                  <b>Biggest leak:</b> {fmtInt(droppedForms)} people start a
                  form and don&apos;t finish. Shortening it to 3 fields could
                  recover a chunk of that.
                </span>
                <Box
                  component="button"
                  type="button"
                  onClick={() => navigate(`${basePath}/lead-form`)}
                  sx={{
                    ml: 'auto',
                    flexShrink: 0,
                    cursor: 'pointer',
                    font: 'inherit',
                    border: 'none',
                    bgcolor: 'transparent',
                    color: t.blue,
                    fontWeight: 800,
                    whiteSpace: 'nowrap',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  Review your forms
                </Box>
              </Box>
            ) : null}
          </Surface>

          {/* ── two columns ── */}
          <Box
            sx={{
              display: 'flex',
              gap: 1.5,
              mt: 1.5,
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'stretch',
            }}
          >
            {/* left */}
            <Box
              sx={{
                flex: 1.55,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                minWidth: 0,
              }}
            >
              <Surface radius="14px" sx={{ p: '15px 16px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography
                    sx={{ fontSize: 13, fontWeight: 800, color: t.text }}
                  >
                    Traffic and leads over time
                  </Typography>
                  <Box sx={{ ml: 'auto', display: 'flex', gap: 1.5 }}>
                    <Legend t={t} color={t.blue} label="Visits" />
                    <Legend t={t} color={t.green} label="Leads" />
                  </Box>
                </Box>
                <TrafficChart t={t} items={tsItems} />
              </Surface>

              <Surface radius="14px" sx={{ p: 0, overflow: 'hidden' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: '13px 16px 11px',
                  }}
                >
                  <Typography
                    sx={{ fontSize: 13, fontWeight: 800, color: t.text }}
                  >
                    Top pages, by revenue not clicks
                  </Typography>
                  <Typography
                    sx={{
                      ml: 'auto',
                      fontSize: 10.5,
                      color: t.faint,
                      fontWeight: 700,
                      letterSpacing: '.4px',
                    }}
                  >
                    VISITS &middot; LEADS &middot; REVENUE
                  </Typography>
                </Box>
                {(attribution?.topPages || []).length === 0 ? (
                  <EmptyRow
                    t={t}
                    text="No traced revenue yet for this range."
                  />
                ) : (
                  attribution.topPages.map((p) => (
                    <Box
                      key={p.path}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 56px 44px 76px',
                        gap: 1.25,
                        alignItems: 'center',
                        p: '11px 16px',
                        borderTop: `1px solid ${t.hairline}`,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 12.5,
                          fontWeight: 800,
                          color: t.text,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={p.path}
                      >
                        {p.path}
                      </Typography>
                      <Typography
                        sx={{
                          textAlign: 'right',
                          fontSize: 12.5,
                          color: t.textSecondary,
                          fontWeight: 600,
                        }}
                      >
                        {viewsByPath[p.path]
                          ? fmtInt(viewsByPath[p.path])
                          : '-'}
                      </Typography>
                      <Typography
                        sx={{
                          textAlign: 'right',
                          fontSize: 12.5,
                          fontWeight: 800,
                          color: t.text,
                        }}
                      >
                        {fmtInt(p.customers)}
                      </Typography>
                      <Typography
                        sx={{
                          textAlign: 'right',
                          fontSize: 13,
                          fontWeight: 900,
                          color: t.greenText,
                        }}
                      >
                        {fmtUsdShort(p.revenueCents)}
                      </Typography>
                    </Box>
                  ))
                )}
              </Surface>
            </Box>

            {/* right */}
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                minWidth: 0,
              }}
            >
              <Surface radius="14px" sx={{ p: '14px 16px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    sx={{ fontSize: 13, fontWeight: 800, color: t.text }}
                  >
                    Where visitors come from
                  </Typography>
                  <Typography
                    sx={{
                      ml: 'auto',
                      fontSize: 10.5,
                      color: t.faint,
                      fontWeight: 700,
                    }}
                  >
                    LEADS &middot; $
                  </Typography>
                </Box>
                {(attribution?.channels || []).length === 0 ? (
                  <Typography sx={{ fontSize: 12.5, color: t.muted, mt: 1.5 }}>
                    No traced revenue yet for this range.
                  </Typography>
                ) : (
                  <Box
                    sx={{
                      mt: 1.5,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.375,
                    }}
                  >
                    {attribution.channels.map((c) => {
                      const pct =
                        channelTotalCust > 0
                          ? Math.round(
                              (num(c.customers) / channelTotalCust) * 100
                            )
                          : 0;
                      const ai = c.channel === 'AI assistants';
                      return (
                        <Box key={c.channel}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'baseline',
                              gap: 1,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: 12,
                                fontWeight: 700,
                                color: t.text,
                              }}
                            >
                              {c.channel}
                            </Typography>
                            {ai ? (
                              <Box
                                component="span"
                                sx={{
                                  fontSize: 8.5,
                                  fontWeight: 800,
                                  color: t.purple,
                                  bgcolor: t.purpleBg,
                                  border: `1px solid ${t.purpleBorder}`,
                                  px: '5px',
                                  borderRadius: '4px',
                                }}
                              >
                                NEW
                              </Box>
                            ) : null}
                            <Typography
                              sx={{
                                fontSize: 11,
                                color: t.faint,
                                fontWeight: 600,
                              }}
                            >
                              {pct}%
                            </Typography>
                            <Typography
                              sx={{
                                ml: 'auto',
                                fontSize: 11,
                                fontWeight: 800,
                                color: t.greenText,
                              }}
                            >
                              {fmtInt(c.customers)} &middot;{' '}
                              {fmtUsdShort(c.revenueCents)}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              height: 6,
                              borderRadius: '4px',
                              bgcolor: t.hairline,
                              mt: '5px',
                              overflow: 'hidden',
                            }}
                          >
                            <Box
                              sx={{
                                width: `${pct}%`,
                                height: '100%',
                                background: ai
                                  ? 'linear-gradient(90deg,#7c3aed,#ab2ddd)'
                                  : t.blue,
                              }}
                            />
                          </Box>
                          {ai && (c.engines || []).length > 0 ? (
                            <Box
                              sx={{
                                mt: '5px',
                                pl: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '3px',
                              }}
                            >
                              {c.engines.map((e) => (
                                <Box
                                  key={e.engine}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'baseline',
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: 10.5,
                                      color: t.muted,
                                      fontWeight: 600,
                                    }}
                                  >
                                    {e.label}
                                  </Typography>
                                  <Typography
                                    sx={{
                                      ml: 'auto',
                                      fontSize: 10.5,
                                      color: t.muted,
                                      fontWeight: 700,
                                    }}
                                  >
                                    {fmtInt(e.customers)} &middot;{' '}
                                    {fmtUsdShort(e.revenueCents)}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          ) : null}
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Surface>

              <Surface radius="14px" sx={{ p: '14px 16px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    sx={{ fontSize: 13, fontWeight: 800, color: t.text }}
                  >
                    Searches that found you
                  </Typography>
                  {isDemo ? (
                    <Box sx={{ ml: 'auto' }}>
                      <SampleChip t={t} show />
                    </Box>
                  ) : null}
                </Box>
                {isDemo ? (
                  <Box
                    sx={{
                      mt: 1.375,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.125,
                    }}
                  >
                    {SAMPLE_SEARCHES.map((s) => (
                      <Box
                        key={s.term}
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: t.textSecondary,
                            flex: 1,
                            minWidth: 0,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {s.term}
                        </Typography>
                        <Typography
                          sx={{ fontSize: 11, color: t.faint, fontWeight: 600 }}
                        >
                          {s.visits}
                        </Typography>
                        <Box
                          component="span"
                          sx={{
                            fontSize: 10.5,
                            fontWeight: 800,
                            color: s.leads > 1 ? t.greenText : t.faint,
                            bgcolor: s.leads > 1 ? t.greenBg : 'transparent',
                            border:
                              s.leads > 1
                                ? `1px solid ${t.greenBorder}`
                                : 'none',
                            px: s.leads > 1 ? '6px' : 0,
                            py: '1px',
                            borderRadius: '5px',
                          }}
                        >
                          {s.leads} {s.leads === 1 ? 'lead' : 'leads'}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography sx={{ fontSize: 12, color: t.muted, mt: 1.375 }}>
                    Search-term data appears once search-engine referrer
                    tracking is enabled for your site.
                  </Typography>
                )}
              </Surface>
            </Box>
          </Box>

          {/* ── bottom row ── */}
          <Box
            sx={{
              display: 'flex',
              gap: 1.5,
              mt: 1.5,
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'stretch',
            }}
          >
            {/* service area */}
            <Surface
              radius="14px"
              sx={{ flex: 1.5, p: '14px 16px', minWidth: 0 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  sx={{ fontSize: 13, fontWeight: 800, color: t.text }}
                >
                  Where they are, service area
                </Typography>
                {isDemo ? (
                  <Box sx={{ ml: 'auto' }}>
                    <SampleChip t={t} show />
                  </Box>
                ) : null}
              </Box>
              {isDemo ? (
                <>
                  <Box
                    sx={{
                      mt: 1.5,
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: 1,
                    }}
                  >
                    {SAMPLE_AREA.map((a) => (
                      <Box key={a.town}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                          <Typography
                            sx={{
                              fontSize: 12,
                              fontWeight: 700,
                              flex: 1,
                              color: a.out ? t.amber : t.text,
                            }}
                          >
                            {a.town}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 11,
                              fontWeight: 800,
                              color: a.out ? t.amber : t.greenText,
                            }}
                          >
                            {a.leads}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            height: 5,
                            borderRadius: '3px',
                            bgcolor: t.hairline,
                            mt: '4px',
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            sx={{
                              width: `${a.pct}%`,
                              height: '100%',
                              bgcolor: a.out ? t.amber : t.blue,
                            }}
                          />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 10.5,
                      color: t.faint,
                      fontWeight: 600,
                      mt: 1.375,
                    }}
                  >
                    Leads from outside your radius are worth a partner referral
                    fee.
                  </Typography>
                </>
              ) : (
                <Typography sx={{ fontSize: 12, color: t.muted, mt: 1.5 }}>
                  Service-area breakdown appears once visitor location data is
                  captured for your site.
                </Typography>
              )}
            </Surface>

            {/* devices */}
            <Surface radius="14px" sx={{ flex: 1, p: '14px 16px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  sx={{ fontSize: 13, fontWeight: 800, color: t.text }}
                >
                  Devices
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-end',
                  gap: 1.25,
                  mt: 1.75,
                  // Room for the tallest column: % label (~18px) + gap +
                  // bar (max 64px) + gap + device label (~15px). At 76px the
                  // tallest column overflowed upward onto the card title.
                  height: 112,
                }}
              >
                {(devices?.devices || []).map((d, i) => (
                  <Box
                    key={d.label}
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 0.75,
                      justifyContent: 'flex-end',
                    }}
                  >
                    <Typography
                      sx={{ fontSize: 12, fontWeight: 900, color: t.text }}
                    >
                      {d.pct}%
                    </Typography>
                    <Box
                      sx={{
                        width: '100%',
                        height: Math.max(8, (d.pct / maxDevicePct) * 64),
                        borderRadius: '6px 6px 0 0',
                        background:
                          i === 0
                            ? 'linear-gradient(180deg,#2563eb,#3b82f6)'
                            : i === 1
                              ? t.muted
                              : t.grayBorder,
                      }}
                    />
                    <Typography
                      sx={{ fontSize: 10, color: t.muted, fontWeight: 700 }}
                    >
                      {d.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Typography
                sx={{
                  fontSize: 10.5,
                  color: t.faint,
                  fontWeight: 600,
                  mt: 1.5,
                }}
              >
                Mobile-first: keep the call button thumb-reachable.
              </Typography>
            </Surface>

            {/* when leads arrive */}
            <Surface radius="14px" sx={{ flex: 1, p: '14px 16px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  sx={{ fontSize: 13, fontWeight: 800, color: t.text }}
                >
                  When leads arrive
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                  mt: 1.625,
                }}
              >
                {(leadTimes?.rows || []).map((row) => (
                  <Box
                    key={row.day}
                    sx={{ display: 'flex', gap: '3px', alignItems: 'center' }}
                  >
                    <Box
                      component="span"
                      sx={{
                        width: 26,
                        fontSize: 9.5,
                        color: t.faint,
                        fontWeight: 700,
                      }}
                    >
                      {row.day}
                    </Box>
                    {row.cells.map((v, i) => (
                      <Box
                        key={i}
                        sx={{
                          flex: 1,
                          height: 11,
                          borderRadius: '2px',
                          bgcolor: heatColor(heatIntensity(v, leadTimes?.max)),
                        }}
                      />
                    ))}
                  </Box>
                ))}
                <Box
                  sx={{
                    display: 'flex',
                    gap: '3px',
                    alignItems: 'center',
                    mt: '2px',
                  }}
                >
                  <Box component="span" sx={{ width: 26 }} />
                  {['AM', 'Noon', 'Eve', 'Late'].map((l) => (
                    <Box
                      key={l}
                      component="span"
                      sx={{
                        flex: 1,
                        fontSize: 8.5,
                        color: t.faint,
                        fontWeight: 600,
                        textAlign: 'center',
                      }}
                    >
                      {l}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Typography
                sx={{
                  fontSize: 10.5,
                  color: t.faint,
                  fontWeight: 600,
                  mt: 1.375,
                }}
              >
                Peak{' '}
                <Box component="span" sx={{ color: t.blue, fontWeight: 700 }}>
                  Tue to Thu, 7 to 9pm
                </Box>
                . Be ready to reply fast.
              </Typography>
            </Surface>
          </Box>

          {/* ── AI crawlers reading your site ── */}
          <Surface radius="14px" sx={{ p: '14px 16px', mt: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 800, color: t.text }}>
                AI crawlers reading your site
              </Typography>
              <Box
                component="span"
                sx={{
                  fontSize: 8.5,
                  fontWeight: 800,
                  color: t.purple,
                  bgcolor: t.purpleBg,
                  border: `1px solid ${t.purpleBorder}`,
                  px: '5px',
                  borderRadius: '4px',
                }}
              >
                AI
              </Box>
            </Box>
            <Typography
              sx={{ fontSize: 11, color: t.faint, fontWeight: 600, mt: '3px' }}
            >
              Training crawls teach AI models about your business. Live
              citations are AI assistants reading a page to answer someone right
              now.
            </Typography>

            {aiBots && num(aiBots.totalHits) > 0 ? (
              <Box
                sx={{
                  mt: 1.5,
                  display: 'flex',
                  gap: 2,
                  flexDirection: { xs: 'column', md: 'row' },
                  alignItems: 'stretch',
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <AiTile
                      t={t}
                      value={fmtInt(aiBots.totalHits)}
                      label={`AI hits · ${aiBots.days || days}d`}
                      accent={t.purple}
                    />
                    <AiTile
                      t={t}
                      value={fmtInt(aiBots.totalTraining)}
                      label="Training crawls"
                    />
                    <AiTile
                      t={t}
                      value={fmtInt(aiBots.totalRuntime)}
                      label="Live citations"
                    />
                  </Box>
                  <MiniSpark
                    items={aiBots.sparkline}
                    acc="hits"
                    color={t.purple}
                  />
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: 10.5,
                      fontWeight: 800,
                      letterSpacing: '.5px',
                      textTransform: 'uppercase',
                      color: t.faint,
                      mb: 0.75,
                    }}
                  >
                    By bot
                  </Typography>
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}
                  >
                    {(aiBots.byBot || []).slice(0, 7).map((b) => {
                      const pct =
                        aiMaxBot > 0
                          ? Math.round((num(b.hits) / aiMaxBot) * 100)
                          : 0;
                      return (
                        <Box key={b.name}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'baseline',
                              gap: 1,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: 12,
                                fontWeight: 700,
                                color: t.text,
                                flex: 1,
                                minWidth: 0,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {b.name}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: 11,
                                fontWeight: 800,
                                color: t.muted,
                              }}
                            >
                              {fmtInt(b.hits)}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              height: 6,
                              borderRadius: '4px',
                              bgcolor: t.hairline,
                              mt: '4px',
                              overflow: 'hidden',
                            }}
                          >
                            <Box
                              sx={{
                                width: `${pct}%`,
                                height: '100%',
                                background:
                                  'linear-gradient(90deg,#7c3aed,#ab2ddd)',
                              }}
                            />
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </Box>
            ) : (
              <Typography sx={{ fontSize: 12, color: t.muted, mt: 1.5 }}>
                No AI crawler activity recorded yet. As ChatGPT, Claude,
                Perplexity, and Google&apos;s AI crawl your site, they will show
                up here.
              </Typography>
            )}
          </Surface>
        </>
      )}
    </Box>
  );
}

// ── small presentational helpers ───────────────────────────────────────────

function SampleChip({ t, show }) {
  if (!show) return null;
  return (
    <Box
      component="span"
      sx={{
        fontSize: 9,
        fontWeight: 800,
        letterSpacing: '.4px',
        color: t.amberText,
        bgcolor: t.amberBg,
        border: `1px solid ${t.amberBorder}`,
        px: '6px',
        py: '1px',
        borderRadius: '4px',
        textTransform: 'uppercase',
      }}
    >
      Sample
    </Box>
  );
}

function AiTile({ t, value, label, accent }) {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        p: '8px 10px',
        borderRadius: '10px',
        bgcolor: t.subtle,
        border: `1px solid ${t.hairline}`,
      }}
    >
      <Typography
        sx={{
          fontSize: 17,
          fontWeight: 900,
          letterSpacing: '-0.5px',
          color: accent || t.text,
        }}
      >
        {value}
      </Typography>
      <Typography sx={{ fontSize: 10, color: t.faint, fontWeight: 600 }}>
        {label}
      </Typography>
    </Box>
  );
}

function Legend({ t, color, label }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.625,
        fontSize: 10.5,
        fontWeight: 700,
        color: t.muted,
      }}
    >
      <Box sx={{ width: 9, height: 3, borderRadius: '3px', bgcolor: color }} />
      {label}
    </Box>
  );
}

function MiniSpark({ items, acc, color }) {
  const pts = (items || []).map((r) => Number(r[acc]) || 0);
  if (pts.length < 2) return <Box sx={{ height: 22, mt: 0.875 }} />;
  const max = Math.max(1, ...pts);
  const step = 120 / (pts.length - 1);
  const d = pts
    .map(
      (v, i) => `${(i * step).toFixed(1)},${(22 - (v / max) * 18).toFixed(1)}`
    )
    .join(' ');
  return (
    <Box sx={{ mt: 0.875, lineHeight: 0 }}>
      <svg
        width="100%"
        height="22"
        viewBox="0 0 120 22"
        preserveAspectRatio="none"
        style={{ display: 'block' }}
      >
        <polyline
          points={d}
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      </svg>
    </Box>
  );
}

function KpiCard({ t, label, value, spark, acc, color, foot, gradient }) {
  if (gradient) {
    return (
      <Box
        sx={{
          background: 'linear-gradient(120deg,#2563eb,#7c3aed)',
          borderRadius: '12px',
          p: '12px 15px',
          color: '#fff',
        }}
      >
        <Typography sx={{ fontSize: 11, fontWeight: 700, opacity: 0.85 }}>
          {label}
        </Typography>
        <Typography
          sx={{
            fontSize: 22,
            fontWeight: 900,
            letterSpacing: '-0.5px',
            mt: '3px',
          }}
        >
          {value}
        </Typography>
        {foot ? (
          <Typography
            sx={{ fontSize: 10.5, fontWeight: 600, opacity: 0.85, mt: 1.125 }}
          >
            {foot}
          </Typography>
        ) : null}
      </Box>
    );
  }
  return (
    <Surface radius="12px" sx={{ p: '12px 14px' }}>
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: t.muted }}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 22,
          fontWeight: 900,
          letterSpacing: '-0.5px',
          mt: '3px',
          color: t.text,
        }}
      >
        {value}
      </Typography>
      {spark ? <MiniSpark items={spark} acc={acc} color={color} /> : null}
      {foot ? (
        <Typography
          sx={{ fontSize: 10.5, color: t.faint, fontWeight: 600, mt: 1.125 }}
        >
          {foot}
        </Typography>
      ) : null}
    </Surface>
  );
}

function FunnelRow({ t, stages }) {
  const maxVal = Math.max(1, ...stages.map((s) => s.value));
  const flexFor = (v) => 0.7 + Math.sqrt(Math.max(0, v) / maxVal) * 2.6;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        gap: 1,
        mt: 1.75,
        flexWrap: { xs: 'wrap', md: 'nowrap' },
      }}
    >
      {stages.map((s, i) => {
        const tone = s.tone;
        const isGrad = tone === 'gradient';
        const bg = isGrad
          ? undefined
          : tone === 'purple'
            ? t.purpleBg
            : tone === 'green'
              ? t.greenBg
              : t.blueBg;
        const border =
          tone === 'purple'
            ? t.purpleBorder
            : tone === 'green'
              ? t.greenBorder
              : t.blueBorder;
        const labelColor =
          tone === 'purple' ? t.purple : tone === 'green' ? t.green : t.blue;
        return (
          <React.Fragment key={s.key}>
            <Box
              sx={{
                flex: { xs: '1 1 45%', md: flexFor(s.value) },
                display: 'flex',
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  borderRadius: '10px',
                  p: '11px 13px',
                  ...(isGrad
                    ? {
                        background: 'linear-gradient(135deg,#16a34a,#15803d)',
                        color: '#fff',
                      }
                    : { bgcolor: bg, border: `1px solid ${border}` }),
                }}
              >
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: isGrad ? 'rgba(255,255,255,.9)' : labelColor,
                  }}
                >
                  {s.label}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 21,
                    fontWeight: 900,
                    letterSpacing: '-0.5px',
                    mt: '2px',
                    color: isGrad ? '#fff' : t.text,
                  }}
                >
                  {s.money ? fmtUsdShort(s.value) : fmtInt(s.value)}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: s.warn ? 700 : 600,
                    mt: '3px',
                    color: isGrad
                      ? 'rgba(255,255,255,.9)'
                      : s.warn
                        ? t.amber
                        : t.muted,
                  }}
                >
                  {s.sub}
                  {s.warn ? ' ⚠' : ''}
                </Typography>
              </Box>
            </Box>
            {i < stages.length - 1 ? (
              <Box
                sx={{
                  display: { xs: 'none', md: 'flex' },
                  alignItems: 'center',
                  color: t.faint,
                  fontSize: 15,
                }}
              >
                &rsaquo;
              </Box>
            ) : null}
          </React.Fragment>
        );
      })}
    </Box>
  );
}

function TrafficChart({ t, items }) {
  const W = 580;
  const H = 150;
  if (!items || items.length < 2) {
    return (
      <Box
        sx={{
          height: H,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: t.faint,
          fontSize: 12,
        }}
      >
        Not enough data in this range yet.
      </Box>
    );
  }
  const visits = items.map((r) => Number(r.uniqueVisitors || r.sessions) || 0);
  const leads = items.map((r) => Number(r.formSubmits) || 0);
  const maxV = Math.max(1, ...visits);
  const maxL = Math.max(1, ...leads);
  const step = W / (items.length - 1);
  const line = (arr, max, top, bottom) =>
    arr
      .map(
        (v, i) =>
          `${(i * step).toFixed(1)},${(bottom - (v / max) * (bottom - top)).toFixed(1)}`
      )
      .join(' ');
  const visitsPts = line(visits, maxV, 30, 120);
  const leadsPts = line(leads, maxL, 92, 138);
  return (
    <Box sx={{ mt: 1.5, lineHeight: 0 }}>
      <svg
        width="100%"
        height={H}
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        style={{ display: 'block' }}
      >
        <defs>
          <linearGradient id="vfill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#2563eb" stopOpacity="0.18" />
            <stop offset="1" stopColor="#2563eb" stopOpacity="0" />
          </linearGradient>
        </defs>
        <line
          x1="0"
          y1="38"
          x2={W}
          y2="38"
          stroke={t.hairline}
          strokeWidth="1"
        />
        <line
          x1="0"
          y1="76"
          x2={W}
          y2="76"
          stroke={t.hairline}
          strokeWidth="1"
        />
        <line
          x1="0"
          y1="114"
          x2={W}
          y2="114"
          stroke={t.hairline}
          strokeWidth="1"
        />
        <polygon points={`${visitsPts} ${W},${H} 0,${H}`} fill="url(#vfill)" />
        <polyline
          points={visitsPts}
          fill="none"
          stroke="#2563eb"
          strokeWidth="2.5"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        <polyline
          points={leadsPts}
          fill="none"
          stroke="#16a34a"
          strokeWidth="2.5"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      </svg>
    </Box>
  );
}

function EmptyRow({ t, text }) {
  return (
    <Typography
      sx={{
        fontSize: 12.5,
        color: t.muted,
        p: '11px 16px',
        borderTop: `1px solid ${t.hairline}`,
      }}
    >
      {text}
    </Typography>
  );
}

// Raw lead count -> 0..3 intensity, relative to the busiest cell. Any
// nonzero count shows at least intensity 1 so a real lead never reads empty.
function heatIntensity(count, max) {
  const c = Number(count) || 0;
  const m = Number(max) || 0;
  if (c <= 0 || m <= 0) return 0;
  return Math.max(1, Math.round((c / m) * 3));
}

function heatColor(v) {
  // 0..3 intensity mapped onto blue tints (stable in both themes).
  const ramp = ['#eff6ff', '#bfdbfe', '#60a5fa', '#1d4ed8'];
  return ramp[Math.max(0, Math.min(3, v))];
}
